import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { FeatureFlagFieldType } from '@vendasta/meetings';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { BookMeNowStoreService } from './book-me-now-store.service';

@Injectable({
  providedIn: 'root',
})
export class CancelFeatureFlagGuard {
  constructor(
    private readonly bookmenowService: BookMeNowStoreService,
    private readonly router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    const meetingId = route.paramMap.get('meetingId');
    const authToken = route.queryParams.authToken;
    // Get external calendarId from route param or hostId query param
    const externalCalendarId =
      route.paramMap.get('calendarId') || route.queryParams.hostId;

    if (!meetingId || !authToken || !externalCalendarId) {
      return of(true);
    }

    // Prepare query params, excluding hostId and authToken since they're not needed in new route
    const queryParams = { ...route.queryParams };
    delete queryParams.hostId;
    delete queryParams.authToken;

    // Load calendar to get the actual calendar ID (MCAL-...) from the external ID (ACTX_...)
    return this.bookmenowService.loadCalendar(externalCalendarId).pipe(
      switchMap((calendar) => {
        const calendarId = calendar.id;

        if (!calendarId) {
          return of(true);
        }

        return this.checkFeatureFlagAndRedirect(
          calendarId,
          meetingId,
          queryParams
        );
      }),
      catchError(() => of(true))
    );
  }

  private checkFeatureFlagAndRedirect(
    calendarId: string,
    meetingId: string,
    queryParams: { [key: string]: string }
  ): Observable<boolean | UrlTree> {
    return this.bookmenowService
      .checkFeatureFlag({
        fieldType: FeatureFlagFieldType.FEATURE_FLAG_FIELD_TYPE_CALENDAR_ID,
        fieldValue: calendarId,
        featureFlagKey: 'meeting_scheduler_sms_notification',
      })
      .pipe(
        map((isEnabled) => {
          if (isEnabled) {
            return this.router.createUrlTree(['/cancel-info', meetingId], {
              queryParams,
            });
          }
          return true;
        }),
        catchError(() => of(true))
      );
  }
}
